import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Button from '../../components/atoms/Button';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import AvatarOverflow from '../../components/molecules/AvatarOverflow';
import Popover from '../../components/molecules/Popover';
import SearchTextField from '../../components/molecules/SearchTextField/SearchTextField';
import Tooltip from '../../components/molecules/Tooltip';
import ConnectionLogo, { ConnectionLogoSizes } from './ConnectionLogo';
import ConnectionRow from './ConnectionRow';
import { SEARCH_MIN_ITEMS, TRANSLATION_KEY, TRIGGER_MAX_LOGOS, logoTooltipKey } from '../constants';
import { connectionItemShape } from '../propShapes';
import css from './ConnectionsMenu.css';

const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };

const connectedFirst = items => [...items].sort((first, second) => Number(second.connected) - Number(first.connected));

const ConnectionsMenu = ({ items, loadError, onRetry }) => {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    if (!items.length && !loadError) return null;

    const sortedItems = connectedFirst(items);
    const hiddenCount = items.length - TRIGGER_MAX_LOGOS;
    const showSearch = items.length >= SEARCH_MIN_ITEMS;
    const normalizedQuery = query.trim().toLowerCase();
    const visibleItems =
        showSearch && normalizedQuery
            ? sortedItems.filter(item => item.name.toLowerCase().includes(normalizedQuery))
            : sortedItems;

    return (
        <>
            <span ref={triggerRef} className={css.triggerAnchor}>
                <Button
                    type="flat"
                    className={css.trigger}
                    ariaExpanded={open}
                    onClick={() => setOpen(isOpen => !isOpen)}
                >
                    <span className={css.triggerContent}>
                        <span className={css.logoStack}>
                            {sortedItems.slice(0, TRIGGER_MAX_LOGOS).map(({ id, name, Logo, connected }) => (
                                <Tooltip
                                    key={id}
                                    wrapperClass={classNames(css.stackItem, { [css.connected]: connected })}
                                    titleTranslationKey={logoTooltipKey(connected)}
                                    titleTranslationData={{ name }}
                                >
                                    <ConnectionLogo Logo={Logo} connected={connected} size={ConnectionLogoSizes.SMALL} />
                                </Tooltip>
                            ))}
                            {hiddenCount > 0 && <AvatarOverflow count={hiddenCount} className={css.overflow} />}
                        </span>
                        <Translate id={`${TRANSLATION_KEY}.TRIGGER`} />
                        <Icon
                            name="chevron-down"
                            size={IconSizes.X_SMALL}
                            interactive={false}
                            className={css.chevron}
                        />
                    </span>
                </Button>
            </span>
            <Popover
                showPopover={open}
                anchorEl={triggerRef.current}
                anchorOrigin={MENU_ANCHOR_ORIGIN}
                transformOrigin={MENU_TRANSFORM_ORIGIN}
                PaperProps={{ className: css.panel }}
                onClose={() => setOpen(false)}
            >
                <div className={css.header}>
                    <span className={css.title}>
                        <Translate id={`${TRANSLATION_KEY}.TITLE`} />
                    </span>
                    <span className={css.description}>
                        <Translate id={`${TRANSLATION_KEY}.DESCRIPTION`} />
                    </span>
                </div>
                <div className={css.body}>
                    {loadError && (
                        <div className={css.loadError}>
                            <Translate id={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                            <Button type="link" onClick={onRetry}>
                                <Translate id={`${TRANSLATION_KEY}.RETRY`} />
                            </Button>
                        </div>
                    )}
                    {showSearch && (
                        <SearchTextField
                            search={query}
                            onSearchChange={setQuery}
                            placeholder={`${TRANSLATION_KEY}.SEARCH_PLACEHOLDER`}
                            iconSize={IconSizes.X_SMALL}
                            className={css.search}
                        />
                    )}
                    {visibleItems.map(item => (
                        <ConnectionRow key={item.id} item={item} />
                    ))}
                    {!visibleItems.length && !loadError && (
                        <div className={css.noResults}>
                            <Translate id={`${TRANSLATION_KEY}.NO_RESULTS`} />
                        </div>
                    )}
                </div>
            </Popover>
        </>
    );
};

ConnectionsMenu.propTypes = {
    items: PropTypes.arrayOf(connectionItemShape).isRequired,
    loadError: PropTypes.bool.isRequired,
    onRetry: PropTypes.func.isRequired,
};

export default ConnectionsMenu;
